<script>
  import { token } from "./store/token.js";
  import { permissions } from "./store/permissions.js";
  import { apiRequest } from "./api/request.js";

  let adminUsers = [];
  let availableRoles = [];
  let loading = true;
  let error = null;
  let currentUserEmail = null;

  // Modal state
  let showModal = false;
  let modalMode = "create"; // 'create' | 'edit'
  let editingUser = null;
  let formEmail = "";
  let formRole = "";
  let formError = "";
  let submitting = false;

  // Delete confirmation
  let showDeleteConfirm = false;
  let deletingUser = null;
  let deleting = false;

  // Get current user email from permissions
  $: currentUserEmail = $permissions.email || null;

  const fetchAdminUsers = async () => {
    loading = true;
    error = null;
    try {
      const [usersData, rolesData] = await Promise.all([
        apiRequest(`${process.env.API_ROOT}/admin/admins/list`),
        apiRequest(`${process.env.API_ROOT}/admin/admins/roles`)
      ]);
      adminUsers = usersData.admin_users || [];
      availableRoles = rolesData.roles || [];
    } catch (err) {
      error = err.message;
    } finally {
      loading = false;
    }
  };

  const loadData = fetchAdminUsers();

  const openCreateModal = () => {
    modalMode = "create";
    formEmail = "";
    formRole = availableRoles[0] || "sales";
    formError = "";
    editingUser = null;
    showModal = true;
  };

  const openEditModal = (user) => {
    modalMode = "edit";
    formEmail = user.email;
    formRole = user.role;
    formError = "";
    editingUser = user;
    showModal = true;
  };

  const closeModal = () => {
    showModal = false;
    formEmail = "";
    formRole = "";
    formError = "";
    editingUser = null;
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async () => {
    formError = "";

    if (modalMode === "create") {
      if (!formEmail || !validateEmail(formEmail)) {
        formError = "Valid email is required";
        return;
      }
    }

    if (!formRole || !availableRoles.includes(formRole)) {
      formError = "Valid role is required";
      return;
    }

    submitting = true;
    try {
      if (modalMode === "create") {
        await apiRequest(`${process.env.API_ROOT}/admin/admins`, {
          method: "POST",
          body: JSON.stringify({ email: formEmail, role: formRole })
        });
      } else {
        await apiRequest(
          `${process.env.API_ROOT}/admin/admins/${encodeURIComponent(editingUser.email)}`,
          {
            method: "PUT",
            body: JSON.stringify({ role: formRole })
          }
        );
      }
      closeModal();
      await fetchAdminUsers();
    } catch (err) {
      formError = err.message;
    } finally {
      submitting = false;
    }
  };

  const confirmDelete = (user) => {
    deletingUser = user;
    showDeleteConfirm = true;
  };

  const cancelDelete = () => {
    deletingUser = null;
    showDeleteConfirm = false;
  };

  const handleDelete = async () => {
    if (!deletingUser) return;

    deleting = true;
    try {
      await apiRequest(
        `${process.env.API_ROOT}/admin/admins/${encodeURIComponent(deletingUser.email)}`,
        { method: "DELETE" }
      );
      showDeleteConfirm = false;
      deletingUser = null;
      await fetchAdminUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      deleting = false;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "admin":
        return "ds-chip-accent";
      case "sales":
        return "ds-chip-ok";
      case "analyst":
        return "ds-chip-warn";
      default:
        return "";
    }
  };

  // Count admins
  $: adminCount = adminUsers.filter((u) => u.role === "admin").length;
  $: isLastAdmin = adminCount === 1;
</script>

<div class="admin-users">
<div class="ds-page">
  <div class="ds-page-header">
    <div>
      <h1 class="ds-page-title">Admin Users</h1>
      <p class="ds-page-subtitle">Manage who has access to this admin panel</p>
    </div>
    <div class="header-actions">
      <span class="user-count">{adminUsers.length} admin users</span>
      <button class="ds-btn ds-btn-primary" on:click={openCreateModal}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Add Admin
      </button>
    </div>
  </div>

  {#if isLastAdmin}
    <div class="ds-alert alert-warn">
      <span class="alert-warn-content">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
        Only one admin remaining. Add another admin before removing the last one.
      </span>
    </div>
  {/if}

  {#await loadData}
    <div class="ds-card">
      <div class="ds-loading">
        <div class="ds-spinner"></div>
        <p>Loading admin users...</p>
      </div>
    </div>
  {:then}
    {#if error}
      <div class="ds-card">
        <div class="ds-error">
          <p>{error}</p>
          <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={fetchAdminUsers}>Retry</button>
        </div>
      </div>
    {:else}
      <div class="ds-card">
        <table class="ds-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {#each adminUsers as user}
              <tr>
                <td>
                  <div class="email-cell">
                    <span class="email">{user.email}</span>
                    {#if user.email === currentUserEmail}
                      <span class="ds-chip ds-chip-accent chip-you">You</span>
                    {/if}
                  </div>
                </td>
                <td>
                  <span class="ds-chip {getRoleBadgeClass(user.role)}">
                    {user.role}
                  </span>
                </td>
                <td>
                  <div class="actions">
                    <button
                      class="action-btn edit"
                      on:click={() => openEditModal(user)}
                      title="Edit role"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      class="action-btn delete"
                      on:click={() => confirmDelete(user)}
                      disabled={user.email === currentUserEmail || (user.role === "admin" && isLastAdmin)}
                      title={user.email === currentUserEmail
                        ? "Cannot delete yourself"
                        : user.role === "admin" && isLastAdmin
                          ? "Cannot delete last admin"
                          : "Delete user"}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            {/each}
            {#if adminUsers.length === 0}
              <tr>
                <td colspan="3"><div class="ds-empty">No admin users found.</div></td>
              </tr>
            {/if}
          </tbody>
        </table>
      </div>

      <div class="ds-card roles-info">
        <h3 class="ds-card-title">Role Definitions</h3>
        <div class="role-grid">
          <div class="role-card">
            <span class="ds-chip ds-chip-accent">admin</span>
            <p>Full access to all features + admin user management</p>
          </div>
          <div class="role-card">
            <span class="ds-chip ds-chip-ok">sales</span>
            <p>Can manage upgrades and promo codes</p>
          </div>
          <div class="role-card">
            <span class="ds-chip ds-chip-warn">analyst</span>
            <p>Can view analytics and workspace info</p>
          </div>
          <div class="role-card">
            <span class="ds-chip">marketing</span>
            <p>Read-only workspaces, analytics, and promo codes; can extend trials</p>
          </div>
        </div>
      </div>
    {/if}
  {:catch err}
    <div class="ds-card">
      <div class="ds-error">
        <p>Error: {err.message}</p>
        <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={fetchAdminUsers}>Retry</button>
      </div>
    </div>
  {/await}
</div>

<!-- Create/Edit Modal -->
{#if showModal}
  <div class="modal-overlay" on:click={closeModal}>
    <div class="modal" on:click|stopPropagation>
      <button class="modal-close" on:click={closeModal}>&times;</button>
      <h2>{modalMode === "create" ? "Add Admin User" : "Edit Admin User"}</h2>

      <form on:submit|preventDefault={handleSubmit}>
        {#if modalMode === "create"}
          <div class="form-group">
            <label for="email">Email</label>
            <input
              id="email"
              type="email"
              bind:value={formEmail}
              placeholder="admin@example.com"
              disabled={submitting}
            />
          </div>
        {:else}
          <div class="form-group">
            <label>Email</label>
            <div class="static-field">{formEmail}</div>
          </div>
        {/if}

        <div class="form-group">
          <label for="role">Role</label>
          <select id="role" bind:value={formRole} disabled={submitting}>
            {#each availableRoles as role}
              <option value={role}>{role}</option>
            {/each}
          </select>
        </div>

        {#if formError}
          <div class="form-error">{formError}</div>
        {/if}

        <div class="form-actions">
          <button type="button" class="btn-cancel" on:click={closeModal} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" class="btn-submit" disabled={submitting}>
            {#if submitting}
              Saving...
            {:else}
              {modalMode === "create" ? "Create" : "Save Changes"}
            {/if}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Delete Confirmation Modal -->
{#if showDeleteConfirm}
  <div class="modal-overlay" on:click={cancelDelete}>
    <div class="modal modal-confirm" on:click|stopPropagation>
      <h2>Confirm Delete</h2>
      <p>Are you sure you want to remove <strong>{deletingUser?.email}</strong> from admin users?</p>
      <p class="confirm-warning">This action cannot be undone.</p>

      <div class="form-actions">
        <button class="btn-cancel" on:click={cancelDelete} disabled={deleting}>
          Cancel
        </button>
        <button class="btn-danger" on:click={handleDelete} disabled={deleting}>
          {#if deleting}
            Deleting...
          {:else}
            Delete
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}
</div>

<style>
  .admin-users {
    position: relative;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .user-count {
    color: var(--ds-text-muted);
    font-weight: 500;
  }

  /* Warning banner: ds-alert layout with warn tokens (no ds-alert-warn variant exists yet) */
  .alert-warn {
    background: var(--ds-warn-soft);
    border: 1px solid var(--ds-warn);
    color: var(--ds-warn);
  }

  .alert-warn-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .email-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .email {
    font-weight: 500;
  }

  .chip-you {
    font-size: 0.68rem;
    padding: 0.15rem 0.55rem;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
  }

  .action-btn {
    background: none;
    border: 1px solid var(--ds-border-3);
    padding: 0.5rem;
    border-radius: var(--ds-radius-sm);
    cursor: pointer;
    transition: all 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .action-btn.edit {
    color: var(--ds-text-muted);
  }

  .action-btn.edit:hover {
    background: var(--ds-surface-3);
    color: var(--ds-text);
  }

  .action-btn.delete {
    color: var(--ds-danger);
  }

  .action-btn.delete:hover:not(:disabled) {
    background: var(--ds-danger-soft);
    border-color: var(--ds-danger-border);
  }

  .action-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .roles-info {
    margin-top: 2rem;
  }

  .role-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .role-card {
    padding: 1rem;
    background: var(--ds-surface-3);
    border: 1px solid var(--ds-border-2);
    border-radius: var(--ds-radius-md);
  }

  .role-card p {
    margin: 0.5rem 0 0 0;
    font-size: 0.8125rem;
    color: var(--ds-text-muted);
  }
</style>
